import { useState, useMemo, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { type FormField, type FormAnswerValue } from '@/types';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/use-debounce-custom';
import {
  buildDynamicZodSchema,
  getDefaultValueForField,
  isPhoneField,
} from '@/lib/dynamic-form-schema';
import { DynamicFormRenderer } from '@/components/forms/dynamic-form-renderer';

interface RegistrationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketName: string;
  ticketPrice: number;
  formSchema: FormField[] | null;
  onSubmit: (formData: Record<string, FormAnswerValue>) => Promise<void>;
  isInline?: boolean;
  submitButtonText?: string;
  hideSubmitButton?: boolean;
  eventId: string;
  allowDuplicatePhone: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

export interface RegistrationFormHandle {
  submit: () => void;
}

const RegistrationFormDialog = forwardRef<RegistrationFormHandle, RegistrationFormDialogProps>(
  (
    {
      open,
      onOpenChange,
      ticketName,
      ticketPrice,
      formSchema,
      onSubmit,
      isInline = false,
      submitButtonText = 'Register',
      hideSubmitButton = false,
      eventId,
      allowDuplicatePhone,
      onValidationChange,
    },
    ref
  ) => {
    // Build the zod schema once per `formSchema` reference so react-hook-form
    // doesn't get a new resolver on every render.
    const zodValidationSchema = useMemo(() => buildDynamicZodSchema(formSchema), [formSchema]);

    const resolver = useMemo(
      () =>
        zodResolver(zodValidationSchema) as unknown as Resolver<Record<string, FormAnswerValue>>,
      [zodValidationSchema]
    );

    // Seed each field with a controlled empty default so inputs never flip
    // from uncontrolled to controlled on first keystroke.
    const defaults = useMemo(
      () =>
        (formSchema || []).reduce<Record<string, FormAnswerValue>>((acc, f) => {
          acc[f.id] = getDefaultValueForField(f);
          return acc;
        }, {}),
      [formSchema]
    );

    const form = useForm<Record<string, FormAnswerValue>>({
      resolver,
      defaultValues: defaults,
      mode: 'onChange',
    });

    const {
      handleSubmit,
      control,
      reset,
      watch,
      formState: { isValid: isFormValid },
    } = form;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({});
    const [isCheckingPhone, setIsCheckingPhone] = useState<Record<string, boolean>>({});

    useEffect(() => {
      if (onValidationChange) {
        const hasPhoneErrors = Object.values(phoneErrors).some((err) => !!err);
        const isChecking = Object.values(isCheckingPhone).some((v) => v);
        onValidationChange(isFormValid && !hasPhoneErrors && !isChecking);
      }
    }, [isFormValid, phoneErrors, isCheckingPhone, onValidationChange]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(handleFormSubmit)();
      },
    }));

    const handleFormSubmit = async (data: Record<string, FormAnswerValue>) => {
      setIsSubmitting(true);
      try {
        const hasPhoneErrors = Object.values(phoneErrors).some((err) => !!err);
        if (hasPhoneErrors) {
          toast.error(Object.values(phoneErrors).find((err) => !!err));
          setIsSubmitting(false);
          return;
        }

        await onSubmit(data);
        reset();
        onOpenChange(false);
      } catch (error) {
        console.error('Registration error:', error);
        toast.error('Failed to register. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    const phoneFields = useMemo(
      () => (formSchema ? formSchema.filter(isPhoneField) : []),
      [formSchema]
    );

    // Debounced phone uniqueness check — one watcher per phone field.
    const PhoneFieldWatcher = ({ fieldId }: { fieldId: string }) => {
      const value = (watch(fieldId) as string) || '';
      const debouncedValue = useDebounce(value, 500);

      useEffect(() => {
        const checkPhone = async () => {
          if (!debouncedValue || debouncedValue.length < 10 || allowDuplicatePhone !== false) {
            setPhoneErrors((prev) => ({ ...prev, [fieldId]: '' }));
            return;
          }

          setIsCheckingPhone((prev) => ({ ...prev, [fieldId]: true }));
          try {
            const res = await axiosInstance.get(
              `/api/events/${eventId}/check-phone?phone=${encodeURIComponent(debouncedValue)}`
            );
            if (res.data.exists) {
              setPhoneErrors((prev) => ({
                ...prev,
                [fieldId]: `This phone number is already registered for this event.`,
              }));
            } else {
              setPhoneErrors((prev) => ({ ...prev, [fieldId]: '' }));
            }
          } catch (err) {
            console.error('Error checking phone:', err);
          } finally {
            setIsCheckingPhone((prev) => ({ ...prev, [fieldId]: false }));
          }
        };

        checkPhone();
      }, [debouncedValue, fieldId]);

      return null;
    };

    const hasFields = formSchema && formSchema.length > 0;

    const formContent = (
      <Form {...form}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {hasFields ? (
            <div className="space-y-4 px-1">
              {phoneFields.map((f) => (
                <PhoneFieldWatcher key={f.id} fieldId={f.id} />
              ))}
              <DynamicFormRenderer
                fields={formSchema}
                control={control}
                uploadFolder="event-registration-answers"
                onUploadingChange={setUploadingImage}
                renderFieldFooter={(field) => {
                  if (isCheckingPhone[field.id]) {
                    return (
                      <p className="mt-1 text-xs text-primary animate-pulse">
                        Checking phone uniqueness...
                      </p>
                    );
                  }
                  if (phoneErrors[field.id]) {
                    return (
                      <p className="mt-1 text-xs font-medium text-destructive">
                        {phoneErrors[field.id]}
                      </p>
                    );
                  }
                  return null;
                }}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Click {submitButtonText.toLowerCase()} to confirm your attendance.
            </p>
          )}

          {!hideSubmitButton && (
            <div className={`flex justify-end gap-2 pt-4 ${isInline ? '' : 'border-t'}`}>
              {!isInline && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={
                  isSubmitting || uploadingImage || Object.values(isCheckingPhone).some((v) => v)
                }
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitButtonText}
              </Button>
            </div>
          )}
        </form>
      </Form>
    );

    if (isInline) {
      return <div className="space-y-4 p-4 border rounded-xl bg-accent/5">{formContent}</div>;
    }

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Register for {ticketName}</DialogTitle>
            <DialogDescription>
              {ticketPrice > 0 ? `Price: ₹${ticketPrice}` : 'Free'}
            </DialogDescription>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    );
  }
);

RegistrationFormDialog.displayName = 'RegistrationFormDialog';

export default RegistrationFormDialog;
